import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Loader2, FileText } from 'lucide-react';
import { companyAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';

export default function PromotionalFlyerModal({ open, onClose }) {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);

  useEffect(() => {
    if (open) {
      fetchExistingPreference();
    }
  }, [open]);

  const fetchExistingPreference = async () => {
    setInitialLoading(true);
    try {
      const response = await companyAPI.getPromotionalFlyerStatus();
      if (response.data && response.data.data) {
        setEnabled(response.data.data.flyerEnabled || false);
      }
    } catch (error) {
      // It's ok if no data exists yet
      console.log('No existing preference found');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSelection = async (value) => {
    setEnabled(value);
    setSaving(true);
    try {
      await companyAPI.updatePromotionalFlyer({ flyerEnabled: value });
      toast({
        title: "Success",
        description: value
          ? "You've opted in for promotional flyer distribution"
          : "You've opted out of promotional flyer distribution"
      });
      // Modal stays open so user can see the shipping address
    } catch (error) {
      console.error('Error saving preference:', error);
      toast({
        title: "Error",
        description: "Failed to save preference",
        variant: "destructive"
      });
      setEnabled(!value); // Revert on error
    } finally {
      setSaving(false);
    }
  };

  if (initialLoading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-lg">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Promotional Flyer Distribution
          </DialogTitle>
          <DialogDescription>
            Choose whether you'd like to distribute promotional flyers to IFTA attendees
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Information Box */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <p className="text-sm text-gray-700">
              Distribute your company's promotional flyer to all IFTA 2025 attendees.
              This is a great opportunity to increase brand visibility.
            </p>
          </div>

          {/* Selection */}
          <div className="space-y-3">
            <Label>Would you like to participate?</Label>
            <div className="flex gap-3">
              <Button
                variant={enabled ? "default" : "outline"}
                onClick={() => handleSelection(true)}
                disabled={saving}
                className="flex-1"
              >
                {saving && enabled ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Yes'
                )}
              </Button>
              <Button
                variant={!enabled ? "default" : "outline"}
                onClick={() => handleSelection(false)}
                disabled={saving}
                className="flex-1"
              >
                {saving && !enabled ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'No'
                )}
              </Button>
            </div>
          </div>

          {enabled && (
            <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
              <p className="text-sm text-gray-700 font-medium mb-2">Shipping Instructions:</p>
              <p className="text-sm text-gray-600">
                Drishtant<br />
                406 Solus, Hiranandani Estate<br />
                Thane West - 400607<br />
                Maharashtra, India
              </p>
              <div className="mt-3 pt-3 border-t border-amber-200">
                <p className="text-sm text-gray-700 font-medium mb-1">Important:</p>
                <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                  <li>Kindly ensure you mention the company name on the package</li>
                  <li>Please include the contact person's name and phone number on the package</li>
                  <li>Ship 500 single-page flyers by December 1, 2025</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}